import {
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
  type ReactNode,
} from 'react';
import { useDock } from '../Dock/DockContext';
import {
  ShapeAwareListContext,
  type PanelSide,
  type CutoutGeometry,
  type ShapeAwareListContextValue,
} from './ShapeAwareListContext';
import {
  calculateCutoutGeometry,
  measureBaseDockDimensions,
  getCSSVariablePixels,
  type BaseDockDimensions,
} from './shapeAwareUtils';
import './shape-aware-list.css';

/**
 * =============================================================================
 * SHAPE-AWARE LIST COMPONENT
 * =============================================================================
 *
 * A wrapper component that enables child list items to dynamically morph their
 * shape to follow the container's dock cutout using CSS clip-path polygons.
 *
 * This component:
 * 1. Measures the dock and container geometry
 * 2. Provides geometry context to child ShapeAwareItem components
 * 3. Updates on scroll, resize, and dock hover state changes
 * 4. Enables smooth transitions when the cutout shape changes
 *
 * =============================================================================
 */

interface ShapeAwareListProps {
  /** Which panel this list is in (left = Notes, right = Gauge) */
  panelSide: PanelSide;
  /** Child elements to render */
  children: ReactNode;
  /** Additional CSS class names */
  className?: string;
  /** Whether to enable shape awareness (can disable for performance) */
  enabled?: boolean;
}

/**
 * ShapeAwareList Component
 */
export function ShapeAwareList({
  panelSide,
  children,
  className = '',
  enabled = true,
}: ShapeAwareListProps): JSX.Element {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isDockHovered } = useDock();

  // Geometry state
  const [geometry, setGeometry] = useState<CutoutGeometry | null>(null);
  const baseDockDimensions = useRef<BaseDockDimensions | null>(null);
  // Container clip-path state - clips the entire list to match dock cutout
  const [containerClipPath, setContainerClipPath] = useState<string>('');
  // Border path for SVG overlay (follows the same shape as clip-path)
  const [borderPath, setBorderPath] = useState<string>('');

  // Edge padding from CSS
  const edgePadding = useMemo(() => {
    return getCSSVariablePixels('--fluid-space-sm', 10);
  }, []);

  /**
   * Measure and update geometry
   */
  const updateGeometry = useCallback(() => {
    if (!enabled || !scrollContainerRef.current) {
      return;
    }

    const dockElement = document.querySelector('.dock-container');
    if (!dockElement) {
      return;
    }

    const contentPanelsElement = document.querySelector('.content-panels');
    if (!contentPanelsElement) {
      return;
    }

    const containerRect = contentPanelsElement.getBoundingClientRect();
    const dockRect = dockElement.getBoundingClientRect();

    // Store base dock dimensions once (when not hovered)
    if (!baseDockDimensions.current && !isDockHovered) {
      baseDockDimensions.current = measureBaseDockDimensions(dockElement, isDockHovered);
    }

    // Fallback: if hovered on first render, estimate base dimensions
    if (!baseDockDimensions.current) {
      baseDockDimensions.current = measureBaseDockDimensions(dockElement, isDockHovered);
    }

    // Calculate geometry
    const newGeometry = calculateCutoutGeometry(
      containerRect,
      dockRect,
      baseDockDimensions.current,
      isDockHovered
    );

    setGeometry(newGeometry);

    // Calculate container clip-path in LIST-LOCAL coordinates
    // The clip-path follows the dock's rectangular shape with rounded corners
    if (scrollContainerRef.current) {
      const listRect = scrollContainerRef.current.getBoundingClientRect();
      const listTop = listRect.top - containerRect.top;
      const listLeft = listRect.left - containerRect.left;
      const listWidth = listRect.width;
      const listHeight = listRect.height;

      // Calculate inner geometry (matching ContentPanels.tsx)
      const innerWrapRadius = newGeometry.wrapRadius + (isDockHovered ? 6 : 10);
      const arcCenterLX = newGeometry.dockLeft + newGeometry.scaledCornerRadius;
      const arcCenterRX = newGeometry.dockRight - newGeometry.scaledCornerRadius;
      const arcCenterTopY = newGeometry.dockTop + newGeometry.scaledCornerRadius;
      const arcCenterBotY = newGeometry.dockBottom - newGeometry.scaledCornerRadius;

      // Inner shelf/floor positions (arc tangent points)
      const innerShelfY = arcCenterTopY - innerWrapRadius;
      const innerFloorY = arcCenterBotY + innerWrapRadius;
      const innerLeftEdge = arcCenterLX - innerWrapRadius;
      const innerRightEdge = arcCenterRX + innerWrapRadius;

      // Transform to list-local coordinates
      const localShelfY = innerShelfY - listTop;
      const localFloorY = Math.min(innerFloorY - listTop, listHeight);
      const localArcTopY = arcCenterTopY - listTop;
      const localArcBotY = arcCenterBotY - listTop;
      const localLeftEdge = innerLeftEdge - listLeft;
      const localRightEdge = innerRightEdge - listLeft;
      const localArcCenterLX = arcCenterLX - listLeft;
      const localArcCenterRX = arcCenterRX - listLeft;

      // Generate clip-path polygon with arc points for dock shape
      let clipPath = '';
      const R = 12; // Corner radius for container corners

      if (panelSide === 'left') {
        // Left panel: cutout is on the right side
        // Check if dock zone is visible in this container
        if (localShelfY > 0 && localShelfY < listHeight) {
          const clampedFloorY = Math.min(localFloorY, listHeight);
          const clampedArcBotY = Math.min(localArcBotY, listHeight);
          const clampedLeftEdge = Math.max(0, Math.min(localLeftEdge, listWidth));
          const clampedArcCenterLX = Math.max(0, Math.min(localArcCenterLX, listWidth));

          // Check if dock cutout extends to the bottom of the list
          const floorAtBottom = clampedFloorY >= listHeight - R;

          if (floorAtBottom) {
            // Floor extends to bottom - polygon goes directly to arc tangent at bottom
            clipPath = `polygon(
              ${R}px 0,
              calc(100% - ${R}px) 0,
              100% ${R}px,
              100% ${localShelfY}px,
              ${clampedLeftEdge}px ${localArcTopY}px,
              ${clampedLeftEdge}px ${clampedArcBotY}px,
              ${clampedArcCenterLX}px 100%,
              ${R}px 100%,
              0 calc(100% - ${R}px),
              0 ${R}px
            )`;
          } else {
            // Normal case - floor is above bottom
            clipPath = `polygon(
              ${R}px 0,
              calc(100% - ${R}px) 0,
              100% ${R}px,
              100% ${localShelfY}px,
              ${clampedLeftEdge}px ${localArcTopY}px,
              ${clampedLeftEdge}px ${clampedArcBotY}px,
              ${clampedArcCenterLX}px ${clampedFloorY}px,
              100% ${clampedFloorY}px,
              100% calc(100% - ${R}px),
              calc(100% - ${R}px) 100%,
              ${R}px 100%,
              0 calc(100% - ${R}px),
              0 ${R}px
            )`;
          }
        } else {
          // Dock zone not visible - simple rounded rectangle
          clipPath = `inset(0 round ${R}px)`;
        }
      } else {
        // Right panel: cutout is on the left side
        if (localShelfY > 0 && localShelfY < listHeight) {
          // Ensure values are within reasonable bounds
          const clampedFloorY = Math.min(localFloorY, listHeight);
          const clampedArcBotY = Math.min(localArcBotY, listHeight);
          const clampedRightEdge = Math.max(0, Math.min(localRightEdge, listWidth));
          const clampedArcCenterRX = Math.max(0, Math.min(localArcCenterRX, listWidth));

          // Check if dock cutout extends to the bottom of the list
          const floorAtBottom = clampedFloorY >= listHeight - R;

          if (floorAtBottom) {
            // Curved L-shape clip using SVG path syntax
            // This follows the dock's rounded corner with proper padding

            // Add padding to create gap between content and dock
            const padding = 12;
            const paddedRightEdge = clampedRightEdge + padding;
            const paddedShelfY = localShelfY - padding;
            const paddedArcTopY = localArcTopY;
            const arcRadius = innerWrapRadius + padding;

            // Build the SVG path string (reused for both clip-path and border)
            const pathD =
              'M 0 0 ' +                                    // Start top-left
              'L ' + listWidth + ' 0 ' +                    // Line to top-right
              'L ' + listWidth + ' ' + listHeight + ' ' +   // Line to bottom-right
              'L ' + paddedRightEdge + ' ' + listHeight + ' ' + // Line left to dock boundary
              'L ' + paddedRightEdge + ' ' + paddedArcTopY + ' ' + // Line up to arc start
              'A ' + arcRadius + ' ' + arcRadius + ' 0 0 0 ' + // Arc (counterclockwise)
                clampedArcCenterRX + ' ' + paddedShelfY + ' ' + // Arc end point
              'L 0 ' + paddedShelfY + ' ' +                 // Line left to edge
              'Z';                                          // Close path

            clipPath = 'path("' + pathD + '")';
            setBorderPath(pathD);
          } else {
            // Normal case - floor is above bottom, include the bottom-left corner
            clipPath = `polygon(
              ${R}px 0,
              calc(100% - ${R}px) 0,
              100% ${R}px,
              100% calc(100% - ${R}px),
              calc(100% - ${R}px) 100%,
              ${R}px 100%,
              0 calc(100% - ${R}px),
              0 ${clampedFloorY}px,
              ${clampedArcCenterRX}px ${clampedFloorY}px,
              ${clampedRightEdge}px ${clampedArcBotY}px,
              ${clampedRightEdge}px ${localArcTopY}px,
              0 ${localShelfY}px,
              0 ${R}px
            )`;
            // Clear border path for non-curved case (uses CSS border instead)
            setBorderPath('');
          }
        } else {
          // Dock zone not visible - simple rounded rectangle
          clipPath = `inset(0 round ${R}px)`;
          setBorderPath('');
        }
      }

      setContainerClipPath(clipPath);
    }
  }, [enabled, isDockHovered, panelSide]);

  // Update geometry on mount and when dock hover state changes
  useEffect(() => {
    if (!enabled) {return;}

    // Initial measurement with delay for layout settling
    const initialTimeout = setTimeout(updateGeometry, 100);
    const settlingTimeout = setTimeout(updateGeometry, 300);

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(settlingTimeout);
    };
  }, [updateGeometry, enabled]);

  // Update geometry when dock hover state changes
  useEffect(() => {
    if (!enabled) {return;}
    updateGeometry();
  }, [isDockHovered, updateGeometry, enabled]);

  // Update geometry on window resize
  useEffect(() => {
    if (!enabled) {return;}

    const handleResize = (): void => {
      // Reset stored dimensions so they get re-measured
      baseDockDimensions.current = null;
      updateGeometry();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateGeometry, enabled]);

  // Update geometry on scroll (for items that enter/exit cutout zone)
  useEffect(() => {
    if (!enabled) {return;}

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {return;}

    let rafId: number | null = null;

    const handleScroll = (): void => {
      // Throttle scroll updates using requestAnimationFrame
      if (rafId) {return;}
      rafId = requestAnimationFrame(() => {
        updateGeometry();
        rafId = null;
      });
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [updateGeometry, enabled]);

  // Context value
  const contextValue: ShapeAwareListContextValue = useMemo(
    () => ({
      panelSide,
      geometry,
      isDockHovered,
      edgePadding,
      scrollContainerRef: scrollContainerRef as React.RefObject<HTMLElement>,
    }),
    [panelSide, geometry, isDockHovered, edgePadding]
  );

  const containerClasses = [
    'shape-aware-list',
    `shape-aware-list--${panelSide}`,
    isDockHovered ? 'shape-aware-list--dock-hovered' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Container styles with clip-path
  const containerStyle: React.CSSProperties = {
    position: 'relative', // For SVG border overlay positioning
    ...(containerClipPath ? { clipPath: containerClipPath } : {}),
    transition: 'clip-path 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  };

  return (
    <ShapeAwareListContext.Provider value={contextValue}>
      {/* Outer wrapper for border SVG (not clipped) */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* SVG border overlay - outside clipped container so it's not clipped */}
        {borderPath && panelSide === 'right' && (
          <svg
            className="shape-aware-border"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          >
            <path
              d={borderPath}
              fill="none"
              stroke="rgba(255, 255, 255, 0.15)"
              strokeWidth="1.5"
            />
          </svg>
        )}
        {/* Clipped scroll container */}
        <div
          ref={scrollContainerRef}
          className={containerClasses}
          style={containerStyle}
        >
          {children}
        </div>
      </div>
    </ShapeAwareListContext.Provider>
  );
}

export default ShapeAwareList;
